import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { MediaDisplay } from '../posts/previews/MediaDisplay';
import { PdfThumb } from '../common/PdfThumb';
import { SocialIcon } from '../icons/SocialIcon';
import type { SocialPlatform } from '../icons/SocialIcon';
import { normalizeMedia } from '../../utils/mediaUtils';

interface DraftPostLike {
  id: string;
  content?: string;
  media?: any;
  platforms?: string[] | Record<string, unknown>;
  brandId?: string;
  brandName?: string;
  brandLogo?: string;
}

interface DraftsCardsProps {
  title?: string;
  description?: string;
  drafts: DraftPostLike[];
  onCreate?: () => void;
}

/**
 * DraftsCards
 *
 * Renders a list of draft posts using the same visual style as the Calendar page's
 * draft sidebar cards. Clicking a card navigates to the post edit page.
 */
export const DraftsCards: React.FC<DraftsCardsProps> = ({ title, description, drafts, onCreate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const resolvePlatforms = (platforms: DraftPostLike['platforms']): string[] => {
    if (!platforms) return [];
    if (Array.isArray(platforms)) return platforms as string[];
    return Object.keys(platforms);
  };

  const handleClick = (draft: DraftPostLike) => {
    if (!draft?.id) return;
    navigate(`/posts/${draft.id}/edit`);
  };

  if (!drafts || drafts.length === 0) {
    return (
      <section className="w-full bg-white rounded-xl border border-black shadow-lg p-3 sm:p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="bg-orange-100 text-orange-700 text-xs font-medium px-2.5 py-0.5 rounded-full">0</span>
          <h3 className="font-semibold text-gray-900">{title || t('dashboard.drafts.title', 'Non planifiés')}</h3>
        </div>
        <p className="text-sm text-gray-500">
          {description || t('dashboard.drafts.description', 'Glissez et déposez ces brouillons sur le calendrier pour les programmer.')}
        </p>
        <div className="min-h-[160px] flex flex-col items-center justify-center text-center">
          <div className="text-sm text-gray-600 mb-4">{t('dashboard.drafts.empty', 'No drafts post need your attention')}</div>
          <button
            onClick={() => (onCreate ? onCreate() : navigate('/content-creation'))}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('sidebar.createContent', 'Create Content')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full bg-white rounded-xl border border-black shadow-lg p-3 sm:p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="bg-orange-100 text-orange-700 text-xs font-medium px-2.5 py-0.5 rounded-full">{drafts.length}</span>
        <h3 className="font-semibold text-gray-900">{title || t('dashboard.drafts.title', 'Non planifiés')}</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        {description || t('dashboard.drafts.description', 'Glissez et déposez ces brouillons sur le calendrier pour les programmer.')}
      </p>

      <div className="space-y-3">
        {drafts.map((draft) => {
          const normalizedMedia = normalizeMedia((draft as any).media);
          const platforms = resolvePlatforms(draft.platforms);
          return (
            <button
              key={draft.id}
              onClick={() => handleClick(draft)}
              className="w-full text-left p-3 border border-black rounded-lg hover:bg-gray-50 transition-colors bg-white"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded overflow-hidden relative">
                  {normalizedMedia ? (
                    (() => {
                      const isPdf = /\.pdf(\?|#|$)/i.test(normalizedMedia.url);
                      return isPdf ? (
                        <PdfThumb src={normalizedMedia.url} className="w-full h-full" frame="square" />
                      ) : (
                        <MediaDisplay
                          mediaUrl={normalizedMedia.url}
                          altText="Post media"
                          type={normalizedMedia.type}
                          className="w-full h-full"
                          objectFit="cover"
                          controls={false}
                          autoPlayOnMount={normalizedMedia.type === 'video'}
                          muted
                        />
                      );
                    })()
                  ) : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 mb-2">
                    <p className="text-sm text-gray-900 line-clamp-2 flex-1">{draft.content || ''}</p>
                    {!normalizedMedia && (
                      <span className="text-[10px] bg-gray-100 px-1 py-0.5 rounded flex-shrink-0">📝</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {draft.brandLogo && (
                      <img src={draft.brandLogo} alt={draft.brandName || 'Brand logo'} className="w-4 h-4 rounded object-cover" />
                    )}
                    <div className="flex gap-1">
                      {platforms.map((platform: string) => (
                        <SocialIcon key={platform} platform={platform as SocialPlatform} size={14} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default DraftsCards;


